"use client";

import { create } from "zustand";
import {
  FileUploadErrorCode,
  getFileUploadErrorMessage,
  isRetryableFileUploadError,
} from "@/lib/file-data/upload-errors";

export type ActiveUploadStatus = "uploading" | "done" | "error";

export interface ActiveUpload {
  id: string;
  fileName: string;
  sizeBytes: number;
  progress: number;
  status: ActiveUploadStatus;
  startedAt: number;
}

export interface FailedUpload {
  id: string;
  fileName: string;
  code: FileUploadErrorCode;
  message: string;
  retryable: boolean;
  failedAt: number;
}

interface UploadManagerState {
  activeUploads: Record<string, ActiveUpload>;
  failedUploads: FailedUpload[];
  start: (upload: { id: string; fileName: string; sizeBytes: number }) => void;
  setProgress: (id: string, progress: number) => void;
  succeed: (id: string) => void;
  fail: (id: string, code: FileUploadErrorCode, message?: string) => void;
  clearFailed: (id?: string) => void;
}

/**
 * Estado de upload de escopo global, e nao por componente.
 *
 * Duas razoes: o aviso de "sair da pagina?" precisa saber se ha upload em
 * andamento em qualquer tela, e a lista de falhas precisa sobreviver ao
 * fechamento do dialogo que originou o upload.
 */
export const useUploadManagerStore = create<UploadManagerState>((set) => ({
  activeUploads: {},
  failedUploads: [],

  start: ({ id, fileName, sizeBytes }) =>
    set((state) => ({
      activeUploads: {
        ...state.activeUploads,
        [id]: {
          id,
          fileName,
          sizeBytes,
          progress: 0,
          status: "uploading",
          startedAt: Date.now(),
        },
      },
      failedUploads: state.failedUploads.filter((failed) => failed.id !== id),
    })),

  setProgress: (id, progress) =>
    set((state) => {
      const current = state.activeUploads[id];

      if (!current) {
        return state;
      }

      return {
        activeUploads: {
          ...state.activeUploads,
          [id]: { ...current, progress },
        },
      };
    }),

  succeed: (id) =>
    set((state) => {
      const rest = { ...state.activeUploads };
      delete rest[id];

      return { activeUploads: rest };
    }),

  fail: (id, code, message) =>
    set((state) => {
      const removed = state.activeUploads[id];
      const rest = { ...state.activeUploads };
      delete rest[id];

      return {
        activeUploads: rest,
        failedUploads: [
          ...state.failedUploads.filter((failed) => failed.id !== id),
          {
            id,
            fileName: removed?.fileName ?? "",
            code,
            message: message ?? getFileUploadErrorMessage(code),
            retryable: isRetryableFileUploadError(code),
            failedAt: Date.now(),
          },
        ],
      };
    }),

  clearFailed: (id) =>
    set((state) => ({
      failedUploads: id
        ? state.failedUploads.filter((failed) => failed.id !== id)
        : [],
    })),
}));

export function useHasActiveUploads(): boolean {
  return useUploadManagerStore(
    (state) => Object.keys(state.activeUploads).length > 0,
  );
}
